/**
 * OnboardingTipOverlay — bubble подсказки, привязанный к DOM якоря.
 *
 * JB 2026-05-26: подсказки должны указывать в реальные элементы. Раньше
 * bubble имел фикс координаты в CSS (top: 64%, left: 50%) → на разных
 * viewports попадал не туда. Теперь: читаем rect якоря через
 * `getAnchorRect`, ставим bubble сразу под/над/сбоку c safe-area
 * clamping чтобы не выезжал за границы экрана.
 *
 * Стрелка нарисована pseudo-элементом, её позиция считается так чтобы
 * указывать в центр якоря (правый/левый/верх/низ — в зависимости от
 * того где bubble оказался относительно anchor).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { getAnchorRect } from './anchors.js';
import { OnboardingAnchorHighlight } from './OnboardingAnchorHighlight.js';
import { OnboardingBackdrop } from './OnboardingBackdrop.js';
import { OnboardingFocusRing } from './OnboardingFocusRing.js';
import { useOnboardingStore } from './store.js';
import type { OnboardingTip, TipAnchor } from './types.js';
import styles from './OnboardingTipOverlay.module.css';

/** Ширина bubble. Реальный max clamps в CSS через max-width. */
const BUBBLE_TARGET_WIDTH = 320;
/** Safe inset от краев экрана. */
const SAFE_MARGIN = 8;
/** Зазор между bubble и якорем. */
const BUBBLE_GAP = 14;

interface Position {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  /** Какая сторона bubble прижата к anchor — отсюда рисуем стрелку. */
  readonly arrow: 'up' | 'down' | 'left' | 'right' | null;
  /** Позиция носика стрелки внутри bubble (0..1). */
  readonly arrowOffset01: number;
}

/**
 * Посчитать позицию bubble такую чтобы:
 *   - Bubble помещался в viewport
 *   - Не пересекался с anchor (gap)
 *   - Был как можно ближе к центру anchor по перпендикулярной оси
 *
 * Heuristic: ставим bubble сверху или снизу якоря (предпочтение —
 * туда где больше места).
 */
function computeBubblePosition(anchorName: TipAnchor, bubbleHeight: number): Position | null {
  // top-banner — статичная позиция вверху экрана, без anchor rect
  if (anchorName === 'top-banner') {
    const vw = window.innerWidth;
    const width = Math.min(BUBBLE_TARGET_WIDTH, vw - SAFE_MARGIN * 2);
    return {
      top: SAFE_MARGIN + 56, // под AtlasToggle
      left: Math.max(SAFE_MARGIN, (vw - width) / 2),
      width,
      arrow: null,
      arrowOffset01: 0.5,
    };
  }

  const rect = getAnchorRect(anchorName);
  if (rect === null) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(BUBBLE_TARGET_WIDTH, vw - SAFE_MARGIN * 2);

  // Anchor center по горизонтали (вертикальная середина не нужна — мы
  // ставим bubble над/под якорем, не сбоку).
  const anchorCenterX = rect.left + rect.width / 2;

  // Решаем: ставить bubble сверху или снизу anchor?
  // Если внизу больше места — снизу. Иначе сверху.
  const spaceBelow = vh - (rect.top + rect.height);
  const spaceAbove = rect.top;
  const placeBelow = spaceBelow >= bubbleHeight + BUBBLE_GAP || spaceBelow >= spaceAbove;

  let top: number;
  let arrow: 'up' | 'down';
  if (placeBelow) {
    top = rect.top + rect.height + BUBBLE_GAP;
    arrow = 'up'; // стрелка bubble указывает ВВЕРХ на anchor (anchor сверху)
  } else {
    top = rect.top - bubbleHeight - BUBBLE_GAP;
    arrow = 'down'; // стрелка вниз — anchor снизу
  }

  // Clamp top в viewport
  top = Math.max(SAFE_MARGIN, Math.min(top, vh - bubbleHeight - SAFE_MARGIN));

  // Горизонтально центрируем по anchor, clamp safe-margin
  let left = anchorCenterX - width / 2;
  left = Math.max(SAFE_MARGIN, Math.min(left, vw - width - SAFE_MARGIN));

  // Стрелка указывает на центр anchor по X. Позиция носика в bubble = (anchorCenterX - left) / width.
  const arrowOffset01 = Math.max(0.1, Math.min(0.9, (anchorCenterX - left) / width));

  return { top, left, width, arrow, arrowOffset01 };
}

export function OnboardingTipOverlay(): JSX.Element {
  const current = useOnboardingStore((s) => s.current);

  return (
    <AnimatePresence>
      {current !== null && (
        <>
          <OnboardingAnchorHighlight key={`hl-${current.id}`} />
          <OnboardingBackdrop key={`bd-${current.id}`} />
          <OnboardingFocusRing key={`ring-${current.id}`} anchor={current.anchor} />
          <TipBubble key={current.id} tip={current} />
        </>
      )}
    </AnimatePresence>
  );
}

interface BubbleProps {
  readonly tip: OnboardingTip;
}

function TipBubble({ tip }: BubbleProps): JSX.Element | null {
  const acknowledge = useOnboardingStore((s) => s.acknowledge);
  const [step, setStep] = useState(0);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    setStep(0);
  }, [tip.id]);

  // Compute position once mounted, then re-poll чтобы ловить resize /
  // scroll / layout shifts + race-condition с регистрацией якоря.
  // 2026-05-26 performance fix: 100ms → 250ms (10×/sec → 4×/sec).
  // На mobile 100ms polling вызывал 10 forced reflows в секунду
  // (getBoundingClientRect) — заметный jank в TG bot. 250ms
  // достаточно быстро для UX и в 2.5 раза меньше reflows.
  useEffect(() => {
    const refresh = (): void => {
      const measuredHeight = bubbleRef.current?.offsetHeight ?? 140;
      setPosition(computeBubblePosition(tip.anchor, measuredHeight));
    };
    refresh();
    const id = window.setInterval(refresh, 250);
    return () => {
      window.clearInterval(id);
    };
  }, [tip.anchor, step]);

  const totalSteps = tip.body.length;
  const isLastStep = step >= totalSteps - 1;
  const currentBody = tip.body[step] ?? '';
  const ackLabel = tip.ack ?? 'понятно';

  function handleNext(): void {
    if (isLastStep) {
      acknowledge();
    } else {
      setStep(step + 1);
    }
  }

  // Стрелка-классы — указывают на anchor
  const arrowClass =
    position?.arrow === 'up'
      ? (styles.arrowUp ?? '')
      : position?.arrow === 'down'
        ? (styles.arrowDown ?? '')
        : '';

  // Стиль bubble — собираем в один объект без промежуточных переменных
  // чтобы избежать конфликта exactOptionalPropertyTypes ↔ MotionStyle при
  // spread'е React.CSSProperties.
  //
  // JB 2026-05-26: fallback позиция = центр viewport через calc() (НЕ
  // transform translate — framer-motion сам управляет transform для
  // animation, residual transform после fallback→computed-перехода ломал
  // позицию bubble после открытия/закрытия AtlasScreen).
  const fallbackWidth = Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 16 : 320);
  const bubbleStyle: Record<string, string | number> =
    position === null
      ? {
          position: 'fixed',
          top: 'calc(50vh - 90px)',
          left: `calc(50vw - ${(fallbackWidth / 2).toString()}px)`,
          width: `${fallbackWidth.toString()}px`,
        }
      : {
          position: 'fixed',
          top: `${position.top.toString()}px`,
          left: `${position.left.toString()}px`,
          width: `${position.width.toString()}px`,
        };
  if (position !== null && position.arrow !== null) {
    bubbleStyle['--arrow-x'] = `${(position.arrowOffset01 * 100).toFixed(1)}%`;
  }

  return (
    <motion.div
      ref={bubbleRef}
      className={`${styles.bubble ?? ''} ${arrowClass}`}
      role="dialog"
      aria-modal="false"
      aria-label={tip.title ?? 'Подсказка'}
      /* 2026-05-26 polish: gentle spring bounce при появлении tip'а.
         stiffness 280, damping 18 — мягкий «поп» без агрессии. Игрок
         замечает что подсказка пришла, не пугается. */
      initial={{ opacity: 0, scale: 0.9, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -2 }}
      transition={{
        opacity: { duration: 0.3, ease: 'easeOut' },
        scale: { type: 'spring', stiffness: 280, damping: 18 },
        y: { type: 'spring', stiffness: 280, damping: 18 },
      }}
      style={bubbleStyle}
    >
      {/* JB 2026-05-26: крестик убран. Tip — это туториал, не модал-помощник;
          игрок проходит шаги через «дальше → понятно». */}
      {tip.title !== undefined && <h4 className={styles.title}>{tip.title}</h4>}
      <p className={styles.body}>{currentBody}</p>
      <div className={styles.footer}>
        {totalSteps > 1 && (
          <span className={styles.stepDots} aria-hidden>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                className={`${styles.stepDot ?? ''} ${i === step ? (styles.stepDotActive ?? '') : ''}`}
              />
            ))}
          </span>
        )}
        <button type="button" className={styles.ackButton} onClick={handleNext}>
          {isLastStep ? ackLabel : 'дальше'}
        </button>
      </div>
    </motion.div>
  );
}
